/**
 * Photoreal Château hero — streamed Google Photorealistic 3D Tiles
 * clipped to the landmark (Grace Cathedral–style wow beat).
 * Attribution required · do not permanently cache tile payloads.
 */
import {
  Group,
  MathUtils,
  Mesh,
  PerspectiveCamera,
  Vector3,
  type Object3D,
  type WebGLRenderer,
} from "three";
import { ORIGIN } from "./stops";
import { latLonToLocal } from "./geo";

export type PhotorealHeroHandle = {
  group: Group;
  setEnabled: (on: boolean) => void;
  enabled: () => boolean;
  /** World-space clip center (Château) for hiding twin clutter underneath. */
  clipCenter: Vector3;
  clipRadius: number;
  update: (camera: PerspectiveCamera, renderer: WebGLRenderer) => void;
  dispose: () => void;
};

const CHATEAU = { lat: 46.81196, lon: -71.20536 };
/** Meters — envelope full Cap Diamant masonry (~82 m+ reach from pin) so Peek matches procedural. */
const CLIP_RADIUS = 84;

export async function createPhotorealHero(
  apiKey: string,
): Promise<PhotorealHeroHandle> {
  const { TilesRenderer } = await import("3d-tiles-renderer");
  const {
    GoogleCloudAuthPlugin,
    ReorientationPlugin,
    TileCompressionPlugin,
    TilesFadePlugin,
    GLTFExtensionsPlugin,
  } = await import("3d-tiles-renderer/plugins");
  const { DRACOLoader } = await import(
    "three/addons/loaders/DRACOLoader.js"
  );

  const group = new Group();
  group.name = "chateau-photoreal-hero";
  group.visible = false;

  const tiles = new TilesRenderer();
  tiles.registerPlugin(
    new GoogleCloudAuthPlugin({ apiToken: apiKey, autoRefreshToken: true }),
  );
  tiles.registerPlugin(new TileCompressionPlugin());
  tiles.registerPlugin(new TilesFadePlugin());
  tiles.registerPlugin(
    new GLTFExtensionsPlugin({
      dracoLoader: new DRACOLoader().setDecoderPath(
        "https://www.gstatic.com/draco/versioned/decoders/1.5.6/",
      ),
    }),
  );
  tiles.registerPlugin(
    new ReorientationPlugin({
      lat: ORIGIN.lat * MathUtils.DEG2RAD,
      lon: ORIGIN.lon * MathUtils.DEG2RAD,
      height: 0,
    }),
  );

  // Prefer detail near camera; keep budget modest for hero-only use
  tiles.errorTarget = 6;

  group.add(tiles.group);

  const center = latLonToLocal(CHATEAU.lat, CHATEAU.lon, 0);
  const _world = new Vector3();
  let on = false;

  function clipDistant(root: Object3D) {
    root.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      obj.getWorldPosition(_world);
      const d = Math.hypot(_world.x - center.x, _world.z - center.z);
      obj.visible = d < CLIP_RADIUS;
    });
  }

  return {
    group,
    clipCenter: center.clone(),
    clipRadius: CLIP_RADIUS,
    enabled: () => on,
    setEnabled(next: boolean) {
      on = next;
      group.visible = next;
    },
    update(camera, renderer) {
      if (!on) return;
      camera.updateMatrixWorld();
      tiles.setCamera(camera);
      tiles.setResolutionFromRenderer(camera, renderer);
      tiles.update();
      clipDistant(tiles.group);
    },
    dispose() {
      group.remove(tiles.group);
      tiles.dispose();
    },
  };
}
